import getScoreTypeDetails from 'src/app/shared/utils/score-type-details';

export interface ScoreScale {
  min: number;
  max: number;
  lo: string;
  hi: string;
}

export interface YukkaScore {
  uid: string;
  /** Display name, resolved from the app-wide score registry. */
  label: string;
  /** `scorelabSettings` key that grants a user access to this score. */
  access: string;
  /** Kept explicit rather than derived from the uid so Pendo hooks stay greppable. */
  rowClass: string;
  /** Seed for a freshly selected score — the ESG/EB-SIM family starts at 0. */
  sentimentWeight: number | null;
  /** Range legend; omitted where the scale has no agreed semantics. */
  scale?: ScoreScale;
  /**
   * Info-pane copy. The "proprietary, not editable" line is deliberately absent:
   * the lock banner above the copy already says it.
   */
  description: string[];
}

const RISK_SCALE: ScoreScale = {
  min: 0,
  max: 10,
  lo: 'Not risky',
  hi: 'Very risky',
};

// The nine built-in scores, in display order. Labels are not stored here — they
// come from getScoreTypeDetails so this dialog can never drift from the rest of
// the app (it used to call `bb` "Bull-Bear Daily" on open and "Bull-Bear"
// everywhere else).
const YUKKA_SCORE_DEFS: Omit<YukkaScore, 'label'>[] = [
  {
    uid: 'sentiment',
    access: 'sentimentAccess',
    rowClass: 'option-sentiment-score',
    sentimentWeight: null,
    scale: { min: 0, max: 100, lo: 'Negative', hi: 'Positive' },
    description: [
      'YUKKA Lab’s Sentiment scores detects, for each meaningful part of a ' +
        'sentence (a clause), whether this is positive, negative or neutral. ' +
        'It also detects the entity the sentiment is about (target). The ' +
        'different sentiments about each unique target are aggregated at the ' +
        'sentence and article levels, providing an overall view of the ' +
        'sentiment towards the particular entity.',
      'The sentiment score gives a precise overall picture of all the ' +
        'positive and negative coverage surrounding an entity. The sentiment ' +
        'score is on a scale of 0 to 100 where 100 is completely positive ' +
        'sentiment and 0 is completely negative sentiment.',
    ],
  },
  {
    uid: 'general_risk',
    access: 'generalRiskAccess',
    rowClass: 'option-general-risk-score',
    sentimentWeight: null,
    scale: RISK_SCALE,
    description: [
      'Yukka Lab’s default General Risk Score assesses companies on a range ' +
        'of events. The rating is on a scale of 0 to 10 where 10 is very ' +
        'risky and 0 is not risky.',
      'It’s calculated using our complex algorithm that consider a ' +
        'multitude of events and other factors.',
      'Enable our General Risk Score to assess the riskiness of companies ' +
        'within your portfolio.',
    ],
  },
  {
    uid: 'credit_risk',
    access: 'creditRiskAccess',
    rowClass: 'option-credit-risk-score',
    sentimentWeight: null,
    scale: RISK_SCALE,
    description: [
      'Yukka Lab’s Credit Risk Score assesses companies based on their news ' +
        'coverage for financial events or sector trends indicating negative ' +
        'credit change. The rating is on a scale of 0 to 10 where 10 is very ' +
        'risky and 0 is not risky.',
      'It’s calibrated using machine learning models by our team of data ' +
        'scientists.',
      'Enable our Credit Risk Score to assess the riskiness of companies ' +
        'within your portfolio.',
    ],
  },
  {
    uid: 'esg_risk',
    access: 'esgRiskAccess',
    rowClass: 'option-esg-risk-score',
    sentimentWeight: null,
    scale: RISK_SCALE,
    description: [
      'Yukka Lab’s ESG Risk Score assesses companies based on ' +
        'Environmental, Social and Governmental activities that can ' +
        'constitute a risk. The score is on a scale of 0 to 10. A company ' +
        'that is engaging in highly risky activities w.r.t to E, S & G ' +
        'factors would receive a score closer to 10 and visa versa while a ' +
        'rating closer to 0 would imply that there is no known ESG Risks in ' +
        'the news.',
      'Our ESG Risk score considered various events within each category of ' +
        'E, S and G. We then assign different weights and importance and ' +
        'determine the impact of each events before running it through our in ' +
        'house algorithms to determine an overall ESG Risk score.',
      'Enable our ESG Risk Score to assess how the companies on your radar ' +
        'are performing in terms of ESG.',
    ],
  },
  {
    uid: 'immediate_risk',
    access: 'immediateRiskAccess',
    rowClass: 'option-immediate-risk-score',
    sentimentWeight: null,
    scale: RISK_SCALE,
    description: [
      'Yukka Lab’s Immediate Risk Score assesses companies on a range of ' +
        'events that are of immediate risk to a company. The rating is on a ' +
        'scale of 0 to 10 where 10 is very risky and 0 is not risky.',
      'It’s calculated in real-time using our in house algorithm that ' +
        'consider a multitude of events that require immediate attention ' +
        'based on their risk to the health of company.',
      'Enable our Immediate Risk Score to assess the riskiness of companies ' +
        'within your portfolio.',
    ],
  },
  {
    uid: 'esg',
    access: 'esgAccess',
    rowClass: 'option-esg-score',
    sentimentWeight: 0,
    description: [
      'Yukka Lab’s default ESG Score rates companies based on ' +
        'Environmental, Social and Governmental activities. The rating is on ' +
        'a scale of 0 to 10. A company that is contributing positively ' +
        'overall to E, S & G factors would receive a rating closer to 10 and ' +
        'visa versa.',
      'Our ESG score considered various events within each category of E, S ' +
        'and G. We then assign different weights and importance and determine ' +
        'the impact of each events before running it through our in house ' +
        'algorithms to determine an overall ESG score.',
      'Enable our ESG Score to assess how the companies on your radar are ' +
        'performing in terms of ESG.',
    ],
  },
  {
    uid: 'eb_sim',
    access: 'eb_simAccess',
    rowClass: 'option-eb-sim-score',
    sentimentWeight: 0,
    description: [],
  },
  {
    uid: 'eb_sim_risk',
    access: 'eb_simAccess',
    rowClass: 'option-eb-sim-risk-score',
    sentimentWeight: 0,
    scale: RISK_SCALE,
    description: [],
  },
  {
    uid: 'bb',
    access: 'bbAccess',
    rowClass: 'option-bb-score',
    sentimentWeight: null,
    description: [
      'Our Bull Bear score algorithm monitors a vast array of news events ' +
        'to asses probability future stock price moves. The resulting Bull ' +
        'Bear score ranges from 0 to 10, with 0 indicating worst predicted ' +
        'performance and 10 best predicted performance, allowing users to ' +
        'anticipate downtrends as well as investment opportunities of listed ' +
        'companies.',
      'The daily signal is suited for a daily swing trading strategy and ' +
        'focused around short-term volatility.',
    ],
  },
];

export const YUKKA_SCORES: YukkaScore[] = YUKKA_SCORE_DEFS.map((score) => ({
  ...score,
  label: getScoreTypeDetails(score.uid).label,
}));

export const YUKKA_SCORES_BY_UID = new Map(
  YUKKA_SCORES.map((score) => [score.uid, score]),
);
